import { e, getDueDateUrgency, formatRelativeTime, formatCapDate, formatTaskDate, formatTaskDuePill, parseContext, confidenceGlyph, isCapRecent, normalizeStatus, linkify, formatShortDate } from './modules/utils.js';
import { PRESET_COLORS, DEFAULT_SUB_COLUMNS, STATUS_OPTIONS, CTX_TAGS, STATUS_COLORS } from './modules/state.js';
import { SESSION_KEY, SESSION_USER_KEY, initAuth, getAuthHeader, softLogout } from './modules/auth-client.js';
import { loadOverrides, saveOverrides, initStorage, cloudSave, cloudSync } from './modules/storage.js';
import { initCapture, openCapture, setupCaptureListeners } from './modules/capture.js';
import { initSearch } from './modules/search.js';
import { initDelegateClient, isArtifactSnoozed, snoozeArtifact, getSnoozedCount, writeDelegateHistory, refreshDelegateQueue, enqueueCardDelegate, showDelegateToast, openDelegatePrompt, showDiscardUndoToast, showSendConfirmModal, updateCapActivityBadge, getDelegateQueueCache } from './modules/delegate-client.js';
import { initChatClient } from './modules/chat-client.js';
import { initSettings } from './modules/settings.js';
import { initDragDrop, initTaskSortable, destroySortables, createCardSortable, createColumnSortable, saveCardOrder } from './modules/drag-drop.js';
import { boardData, navStack, setNavStack, expandedCards, _pendingTaskFocusId, setPendingTaskFocusId, _pendingCardRenameId, setPendingCardRenameId, _cardFlagsCache, _dismissedFlagsCache, AI_NAME, _suggestionCache, _activeSuggestPollCardId, setActiveSuggestPollCardId, _activeSuggestPollTimer, setActiveSuggestPollTimer, _activeSuggestPollTimeout, setActiveSuggestPollTimeout, _boardDataRef, _activeFocusTrapRef, _focusTrapTriggerRef, _localOverridesVersion, setLocalOverridesVersion, _commsCache, fetchComms, _activeFocusTrap, _focusTrapTrigger, setActiveFocusTrap, setFocusTrapTrigger } from './core.js';
import { getStatusStyle, collectBoardTags, collectAllContextTags, trapFocus } from './ui.js';
import { findCardById, mergeOverrides, getCurrentView, formatSchedulePill, getCardSchedule, getCardLastActivity } from './data.js';
import { flashSaved, flashToast, renameColumn, archiveColumn, deleteColumn, addColumn, duplicateCard, deleteProject, addNewProject, addNewProjectWithId, saveStatus, saveCardDueDate, saveCardEndDate, openDateRangePopover, toggleArchive, isFlagged, countFlaggedDescendants, attachCardRename, startColRename, showUndoToast, isParentCard } from './cards.js';
import { refreshWorkspace, getActiveWorkspace, openWorkspace, renderWins, checkIntentionTransition, isIntentionsActive, applyIntentionSpotlight, isFocusFlowActive } from './views.js';

  function renderBreadcrumb() {
    const bc = document.getElementById('breadcrumb');
    const board = document.getElementById('board');
    if (navStack.length === 0) {
      bc.classList.add('hidden');
      board.classList.remove('has-breadcrumb');
      return;
    }
    bc.classList.remove('hidden');
    board.classList.add('has-breadcrumb');

    let html = '<button class="breadcrumb-back" title="Go up one level">\u2190</button>';
    html += '<button class="breadcrumb-segment" data-nav-index="-1">Re:Focus</button>';
    for (let i = 0; i < navStack.length; i++) {
      html += '<span class="breadcrumb-sep">/</span>';
      const isCurrent = i === navStack.length - 1;
      html += `<button class="breadcrumb-segment${isCurrent ? ' current' : ''}" data-nav-index="${i}">${e(navStack[i].name)}</button>`;
    }
    bc.innerHTML = html;

    bc.querySelector('.breadcrumb-back').addEventListener('click', () => {
      navStack.pop(); renderFromState();
    });
    bc.querySelectorAll('.breadcrumb-segment').forEach(seg => {
      seg.addEventListener('click', () => {
        const idx = parseInt(seg.dataset.navIndex);
        if (idx === -1) setNavStack([]);
        else if (idx < navStack.length - 1) setNavStack(navStack.slice(0, idx + 1));
        else return;
        renderFromState();
      });
    });
  }

  // ── Navigation ──
  function drillInto(cardId, cardName) {
    navStack.push({ id: cardId, name: cardName });
    renderFromState();
  }

  // ── Render ──
  function render(data) {
    const board = document.getElementById('board');
    board.innerHTML = '';
    destroySortables();
    const view = getCurrentView(data);
    const ov = loadOverrides();
    renderBreadcrumb();

    // ── Context columns: full Miller column stack, one per nav level ──
    if (navStack.length > 0) {
      for (let level = 0; level < navStack.length; level++) {
        const levelActiveId = navStack[level].id;
        let levelProjects = [];
        let levelColId = 'ctx-' + level;

        if (level === 0) {
          // Root level: find which root column contains this nav card
          for (const col of data.columns) {
            if (col.projects.find(p => p.id === levelActiveId)) {
              levelProjects = col.projects;
              levelColId = col.id;
              break;
            }
          }
        } else {
          // Show all children of the card at the previous nav level
          const parentId = navStack[level - 1].id;
          const parentCard = findCardById(parentId, data);
          if (parentCard && parentCard.children) {
            levelProjects = parentCard.children;
            levelColId = 'ctx-children-' + parentId;
          }
        }

        if (levelProjects.length === 0) continue;

        const ctxEl = document.createElement('div');
        ctxEl.className = 'column context-column';
        ctxEl.innerHTML = `<div class="column-cards context-cards" data-column-id="${levelColId}"></div>`;
        const ctxCards = ctxEl.querySelector('.context-cards');

        for (const proj of levelProjects) {
          const isActive = proj.id === levelActiveId;
          const archived = ov.archivedCards && ov.archivedCards[proj.id];
          const card = document.createElement('div');
          card.dataset.projectId = proj.id;
          card.style.borderLeftColor = proj.color;

          if (archived) {
            card.className = 'card card-archived';
            card.innerHTML = `<div class="card-top-row"><span class="card-name">${e(proj.name)}</span></div>`;
            ctxCards.appendChild(card);
            continue;
          }

          card.className = `card context-card${isActive ? ' context-card-active' : ''}`;

          const datePill = formatSchedulePill(proj);
          const status = proj.status || '';
          const statusStyle = getStatusStyle(status);
          const flagged = isFlagged(proj.id);
          const flagCount = countFlaggedDescendants(proj, ov);

          if (isParentCard(proj)) {
            // Filter archived and Done children
            const ctxActiveChildren = proj.children.filter(c => !c.archived && !(ov.archivedCards && ov.archivedCards[c.id]) && normalizeStatus(c.status) !== 'Done');
            const childCount = ctxActiveChildren.length;
            // isActive = this card is the current drill target (sub-board is open)
            // When active/spilled: show collapse button (▾), no children list — sub-board IS the list
            // When not active: show children list, drill button (▶)
            card.innerHTML = `
              ${flagged ? '<span class="flag-dot" title="Flagged"></span>' : ''}
              <div class="card-top-row">
                <span class="card-name">${e(proj.name)}</span>
                <span class="card-actions">
                  <button class="card-nested-indicator ctx-nest-btn">${isActive ? `${childCount > 7 ? '>7' : childCount} \u25be` : `\u25b6 ${childCount > 7 ? '>7' : childCount}`}</button>
                </span>
              </div>
              ${(status && status !== 'Active') ? `<div style="margin:4px 0 2px"><span class="ctx-status-pill" style="${statusStyle}">${status}</span></div>` : ''}
              ${datePill ? `<div class="card-date-row">${datePill}</div>` : ''}
              ${!isActive ? `
                <div class="ctx-children-list">
                  ${ctxActiveChildren.map(c => {
                    const cs = normalizeStatus(c.status || '');
                    const csStyle = (cs && cs !== 'Active') ? getStatusStyle(cs) : '';
                    return `<div class="ctx-child-item"><span class="ctx-child-name">${e(c.name)}</span>${(cs && cs !== 'Active') ? `<span class="ctx-status-pill" style="${csStyle}">${cs}</span>` : ''}</div>`;
                  }).join('')}
                </div>` : ''}
            `;
          } else {
            const tasks = proj.tasks || [];
            const remaining = tasks.filter(t => !t.done);
            const nextTask = remaining[0];
            const allDone = remaining.length === 0;
            const expandedContent = isActive && tasks.length > 0 ? `
              <div class="ctx-children-list">
                ${tasks.map(t => `<div class="ctx-child-item${t.done ? ' ctx-child-done' : ''}"><span class="ctx-child-name">${e(t.text)}</span>${t.done ? '<span class="ctx-done-mark">\u2713</span>' : ''}</div>`).join('')}
              </div>` : '';
            card.innerHTML = `
              ${flagged ? '<span class="flag-dot" title="Flagged"></span>' : ''}
              <div class="card-top-row">
                <span class="card-name">${e(proj.name)}</span>
              </div>
              ${(status && status !== 'Active') ? `<div style="margin:4px 0 2px"><span class="ctx-status-pill" style="${statusStyle}">${status}</span></div>` : ''}
              ${datePill ? `<div class="card-date-row">${datePill}</div>` : ''}
              ${!isActive ? `<div class="card-meta"><span class="card-next">${nextTask ? '\u2192 ' + e(nextTask.text) : '\u2713 Complete'}</span><span class="card-remaining${allDone ? ' all-done' : ''}">${allDone ? 'All done' : remaining.length + ' left'}</span></div>` : ''}
              ${expandedContent}
            `;
          }

          const capturedLevel = level;

          // ▶/▾ toggle: ▶ drills in, ▾ collapses back
          const ctxNestBtn = card.querySelector('.ctx-nest-btn');
          if (ctxNestBtn) {
            ctxNestBtn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              if (isActive) {
                // Collapse: pop this level off the stack
                setNavStack(navStack.slice(0, capturedLevel));
              } else {
                // Drill in
                setNavStack(navStack.slice(0, capturedLevel));
                navStack.push({ id: proj.id, name: proj.name });
              }
              renderFromState();
            });
          }

          card.addEventListener('click', () => {
            if (isActive) return;
            setNavStack(navStack.slice(0, capturedLevel));
            navStack.push({ id: proj.id, name: proj.name });
            renderFromState();
          });

          ctxCards.appendChild(card);
        }

        // ── Add Card for this context level ──
        const ctxAddBtn = document.createElement('button');
        ctxAddBtn.className = 'add-card-btn';
        ctxAddBtn.textContent = '+ Add Card';
        const ctxAddForm = document.createElement('div');
        ctxAddForm.className = 'add-card-form hidden';
        ctxAddForm.innerHTML = `
          <input type="text" class="add-card-name" placeholder="Project or commitment\u2026">
          <div class="color-swatches">
            ${PRESET_COLORS.map((c, i) => `<div class="color-swatch${i === 0 ? ' selected' : ''}" data-color="${c}" style="background:${c}"></div>`).join('')}
          </div>
          <label class="sub-items-toggle">
            <input type="checkbox" class="sub-items-check"><span>Has sub-items?</span>
          </label>
          <div class="add-card-actions">
            <button class="add-card-submit">Add</button>
            <button class="add-card-cancel">Cancel</button>
          </div>
        `;
        ctxAddBtn.addEventListener('click', () => {
          ctxAddBtn.classList.add('hidden');
          ctxAddForm.classList.remove('hidden');
          ctxAddForm.querySelector('.add-card-name').value = '';
          ctxAddForm.querySelector('.sub-items-check').checked = false;
          ctxAddForm.querySelectorAll('.color-swatch').forEach((sw, i) => sw.classList.toggle('selected', i === 0));
          ctxAddForm.querySelector('.add-card-name').focus();
        });
        ctxAddForm.querySelectorAll('.color-swatch').forEach(sw => {
          sw.addEventListener('click', () => {
            ctxAddForm.querySelector('.color-swatch.selected')?.classList.remove('selected');
            sw.classList.add('selected');
          });
        });
        const capLevel = level;
        const capColId = level >= 1 ? DEFAULT_SUB_COLUMNS[0].id : levelColId;
        const ctxSubmit = () => {
          const name = ctxAddForm.querySelector('.add-card-name').value.trim();
          if (!name) return;
          const color = ctxAddForm.querySelector('.color-swatch.selected')?.dataset.color || PRESET_COLORS[0];
          const hasSubItems = ctxAddForm.querySelector('.sub-items-check').checked;
          const savedStack = [...navStack];
          setNavStack(navStack.slice(0, capLevel));
          addNewProject(capColId, name, color, hasSubItems);
          setNavStack(savedStack);
          ctxAddForm.classList.add('hidden');
          ctxAddBtn.classList.remove('hidden');
        };
        ctxAddForm.querySelector('.add-card-submit').addEventListener('click', ctxSubmit);
        ctxAddForm.querySelector('.add-card-name').addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); ctxSubmit(); }
          if (e.key === 'Escape') { ctxAddForm.classList.add('hidden'); ctxAddBtn.classList.remove('hidden'); }
        });
        ctxAddForm.querySelector('.add-card-cancel').addEventListener('click', () => {
          ctxAddForm.classList.add('hidden'); ctxAddBtn.classList.remove('hidden');
        });
        ctxEl.appendChild(ctxAddBtn);
        ctxEl.appendChild(ctxAddForm);
        board.appendChild(ctxEl);
      }
    }

    // ── Drilled state setup ──────────────────────────────────────────────────
    const isDrilled = navStack.length > 0;
    document.getElementById('home-btn').classList.toggle('hidden', !isDrilled);
    let sharedCardsContainer = null;

    if (isDrilled) {
      // Single wrapping sub-board — all columns' cards flow into one container
      const subBoard = document.createElement('div');
      subBoard.className = 'sub-board';
      const spineRail = document.createElement('div');
      spineRail.className = 'drill-spine';
      subBoard.appendChild(spineRail);

      // Scroll wrapper — gives iOS a proper native-scrollable block element
      const scrollWrapper = document.createElement('div');
      scrollWrapper.className = 'sub-board-scroll';
      sharedCardsContainer = document.createElement('div');
      sharedCardsContainer.className = 'sub-board-cards';
      scrollWrapper.appendChild(sharedCardsContainer);
      subBoard.appendChild(scrollWrapper);

      // ── Add Item inline form (replaces prompt()) ──
      const parentId = navStack[navStack.length - 1].id;
      const parentCard = findCardById(parentId, data);
      if (parentCard && parentCard.children !== undefined) {
        const addBtn = document.createElement('button');
        addBtn.className = 'add-card-btn sub-board-add';
        addBtn.textContent = '+ Add Item';

        const addForm = document.createElement('div');
        addForm.className = 'add-card-form hidden';
        addForm.innerHTML = `
          <input type="text" class="add-card-name" placeholder="New item\u2026">
          <div class="color-swatches">
            ${PRESET_COLORS.map((c, i) => `<div class="color-swatch${i === 0 ? ' selected' : ''}" data-color="${c}" style="background:${c}"></div>`).join('')}
          </div>
          <label class="sub-items-toggle">
            <input type="checkbox" class="sub-items-check"><span>Has sub-items?</span>
          </label>
          <div class="add-card-actions">
            <button class="add-card-submit">Add</button>
            <button class="add-card-cancel">Cancel</button>
          </div>
        `;

        addBtn.addEventListener('click', () => {
          addBtn.classList.add('hidden');
          addForm.classList.remove('hidden');
          addForm.querySelector('.add-card-name').value = '';
          addForm.querySelector('.sub-items-check').checked = false;
          addForm.querySelectorAll('.color-swatch').forEach((sw, i) => sw.classList.toggle('selected', i === 0));
          addForm.querySelector('.add-card-name').focus();
        });

        addForm.querySelectorAll('.color-swatch').forEach(sw => {
          sw.addEventListener('click', () => {
            addForm.querySelector('.color-swatch.selected')?.classList.remove('selected');
            sw.classList.add('selected');
          });
        });

        const subCols = parentCard.subBoardColumns || DEFAULT_SUB_COLUMNS;
        const submitSubItem = () => {
          const name = addForm.querySelector('.add-card-name').value.trim();
          if (!name) return;
          const color = addForm.querySelector('.color-swatch.selected')?.dataset.color || PRESET_COLORS[0];
          const hasSubItems = addForm.querySelector('.sub-items-check').checked;
          addNewProject(subCols[0].id, name, color, hasSubItems);
          addForm.classList.add('hidden');
          addBtn.classList.remove('hidden');
        };

        addForm.querySelector('.add-card-submit').addEventListener('click', submitSubItem);
        addForm.querySelector('.add-card-name').addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); submitSubItem(); }
          if (e.key === 'Escape') { addForm.classList.add('hidden'); addBtn.classList.remove('hidden'); }
        });
        addForm.querySelector('.add-card-cancel').addEventListener('click', () => {
          addForm.classList.add('hidden'); addBtn.classList.remove('hidden');
        });

        subBoard.appendChild(addBtn);
        subBoard.appendChild(addForm);
      }

      board.appendChild(subBoard);
    }

    for (const col of view.columns) {
      // Sort: all-tasks-done cards sink below active ones; archived cards sink to the very bottom
      const cardSortTier = (card) => {
        if (ov.archivedCards && ov.archivedCards[card.id]) return 2;
        if (!card.children && card.tasks && card.tasks.length > 0 && card.tasks.every(t => t.done)) return 1;
        return 0;
      };
      const sorted = [...col.projects].sort((a, b) => cardSortTier(a) - cardSortTier(b));

      let colEl = null;
      let cardsContainer;
      if (isDrilled) {
        cardsContainer = sharedCardsContainer;
      } else {
        colEl = document.createElement('div');
        colEl.className = 'column';
        colEl.dataset.columnId = col.id;
        colEl.innerHTML = `
          <div class="column-header">
            <span class="col-drag-handle" title="Drag to reorder">⠿</span>
            <span class="col-name" title="Click to rename">${e(col.name)}</span>
            <div class="col-header-right">
              <span class="count">${col.projects.length}</span>
              <button class="col-menu-btn" title="Column options">⋮</button>
            </div>
            <div class="col-menu hidden">
              <button class="col-menu-item col-rename-item">✏️ Rename area</button>
              <button class="col-menu-item col-archive-item">📁 Archive area</button>
              <button class="col-menu-item col-delete-item danger">🗑️ Delete area</button>
            </div>
          </div>
          <div class="column-cards" data-column-id="${col.id}"></div>
          <div class="column-footer"></div>
        `;
        cardsContainer = colEl.querySelector('.column-cards');

        // ── Column rename (click name) ──
        const colNameEl = colEl.querySelector('.col-name');
        colNameEl.addEventListener('click', () => startColRename(colEl, col.id, col.name));

        // ── Column ⋮ menu ──
        const menuBtn = colEl.querySelector('.col-menu-btn');
        const menu = colEl.querySelector('.col-menu');
        menuBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          document.querySelectorAll('.col-menu').forEach(m => { if (m !== menu) m.classList.add('hidden'); });
          menu.classList.toggle('hidden');
        });
        colEl.querySelector('.col-rename-item').addEventListener('click', (e) => {
          e.stopPropagation(); menu.classList.add('hidden');
          startColRename(colEl, col.id, col.name);
        });
        colEl.querySelector('.col-archive-item').addEventListener('click', (e) => {
          e.stopPropagation(); menu.classList.add('hidden');
          archiveColumn(col.id);
        });
        colEl.querySelector('.col-delete-item').addEventListener('click', async (ev) => {
          ev.stopPropagation(); menu.classList.add('hidden');
          if (!confirm(`Delete area "${col.name}"?\n\nThe area must be empty. Archived cards will be removed too.`)) return;
          try {
            const token = localStorage.getItem(SESSION_KEY) || '';
            const resp = await fetch(`/api/v1/columns?id=${encodeURIComponent(col.id)}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
              alert(data.error || `Could not delete area (${resp.status})`);
              return;
            }
            // Also clean up local overrides
            deleteColumn(col.id);
          } catch (err) {
            alert('Delete failed: ' + err.message);
          }
        });
      }

      for (const proj of sorted) {
        const archived = ov.archivedCards && ov.archivedCards[proj.id];
        const card = document.createElement('div');
        card.dataset.projectId = proj.id;
        card.style.borderLeftColor = proj.color;

        // ── Archived minimal render ──
        if (archived) {
          card.className = 'card card-archived';
          const status = proj.status || '';
          const statusStyle = getStatusStyle(status);
          card.innerHTML = `
            <div class="card-top-row card-archived-row">
              <span class="card-drag-handle" title="Drag">\u283f</span>
              <span class="card-name">${e(proj.name)}</span>
              <span class="archived-card-actions">
                <button class="card-unarchive-btn" title="Unarchive">Unarchive</button>
                <span class="card-delete-zone">
                  <button class="card-delete-btn" title="Delete permanently" aria-label="Delete card">\u00d7</button>
                  <span class="card-delete-confirm hidden">
                    <span class="confirm-label">Permanent?</span>
                    <button class="confirm-yes" title="Yes, delete" aria-label="Confirm delete">\u2713</button>
                    <button class="confirm-no" title="Cancel" aria-label="Cancel delete">\u2715</button>
                  </span>
                </span>
              </span>
            </div>
          `;
          card.querySelector('.card-unarchive-btn').addEventListener('click', (e) => {
            e.stopPropagation(); toggleArchive(proj.id);
          });
          card.querySelector('.card-delete-btn').addEventListener('click', (e) => {
            e.stopPropagation();
            const zone = card.querySelector('.card-delete-zone');
            zone.querySelector('.card-delete-btn').classList.add('hidden');
            zone.querySelector('.card-delete-confirm').classList.remove('hidden');
          });
          card.querySelector('.confirm-yes').addEventListener('click', (e) => {
            e.stopPropagation(); deleteProject(proj.id);
          });
          card.querySelector('.confirm-no').addEventListener('click', (e) => {
            e.stopPropagation();
            const zone = card.querySelector('.card-delete-zone');
            zone.querySelector('.card-delete-btn').classList.remove('hidden');
            zone.querySelector('.card-delete-confirm').classList.add('hidden');
          });
          cardsContainer.appendChild(card);
          continue;
        }

        card.className = 'card';

        // Cap badge HTML
        const capBadgeHtml = proj.capUpdatedAt
          ? `<span class="cap-badge${isCapRecent(proj.capUpdatedAt) ? ' cap-recent' : ''}" data-cap-date="${proj.capUpdatedAt}">\ud83e\udee1</span>`
          : '';

        // ── Unified card render (parent + leaf same template) ──
        const isParent = isParentCard(proj);
        const tasks = proj.tasks || [];
        const contacts = proj.contacts || [];
        const comms = proj.comms || [];
        const waitingFor = proj.waitingFor || '';
        const showAll = !!(ov.cardShowAllSections && ov.cardShowAllSections[proj.id]);
        const hasOutcome = !!(proj.outcome || '').trim();
        const hasWaitingFor = !!waitingFor.trim();
        const hasContacts = contacts.length > 0;
        const hasComms = comms.length > 0;
        const progressNotes = (ov.cardProgressNotes && ov.cardProgressNotes[proj.id]) || [];
        const hasProgressNotes = progressNotes.length > 0;
        const autoSec = hasOutcome ? 'outcome' : hasWaitingFor ? 'waitingfor' : hasContacts ? 'contacts' : hasComms ? 'comms' : hasProgressNotes ? 'progressnotes' : null;
        const openSec = (ov.cardActiveSection && proj.id in ov.cardActiveSection) ? ov.cardActiveSection[proj.id] : autoSec;
        const status = proj.status || '';
        const statusStyle = getStatusStyle(status);
        const flagged = isFlagged(proj.id);
        const flagCount = isParent ? countFlaggedDescendants(proj, ov) : 0;
        // Filter archived and Done children for next-action derivation
        const activeChildren = isParent ? proj.children.filter(c => !c.archived && !(ov.archivedCards && ov.archivedCards[c.id]) && normalizeStatus(c.status) !== 'Done') : [];
        const childCount = isParent ? activeChildren.length : 0;
        const datePill = formatSchedulePill(proj);
        const remaining = tasks.filter(t => !t.done);
        const nextTask = remaining[0];
        const allDone = tasks.length > 0 && remaining.length === 0;

        // card-last-done: most recent completedAt from done tasks
        const doneTasks = tasks.filter(t => t.done && t.completedAt);
        const lastDone = doneTasks.length > 0
          ? doneTasks.reduce((a, b) => new Date(a.completedAt) > new Date(b.completedAt) ? a : b)
          : null;
        const lastDoneHtml = lastDone
          ? `<div class="card-last-done">✓ ${formatRelativeTime(lastDone.completedAt)}</div>`
          : '';

        // card-meta: prefer task summary; fall back to outcome snippet; then child summary
        const outcomeSnippet = (proj.outcome || '').trim();
        const metaNext = nextTask
          ? '\u2192 ' + e(nextTask.text)
          : (isParent && activeChildren.length > 0 ? '\u25b8 ' + e(activeChildren[0].name)
            : outcomeSnippet ? '\u2139 ' + e(outcomeSnippet.split('\n')[0].slice(0, 80))
            : '\u2713 No items');
        const metaRight = nextTask
          ? (allDone ? 'All done' : remaining.length + ' left')
          : (isParent ? childCount + ' item' + (childCount !== 1 ? 's' : '') : '');
        const metaRightClass = (allDone && tasks.length > 0) ? 'card-remaining all-done' : 'card-remaining';

        // Is this parent card currently drilled into?
        const isCurrentTarget = isParent && navStack.length > 0 && navStack[navStack.length - 1].id === proj.id;

        // Stale card flag dot (P1-6: respects Cap Ambient mode)
        // BUG-11: Check for expired dismissals — re-show dot if card was updated since dismissal
        let cardFlag = _cardFlagsCache[proj.id];
        if (!cardFlag && _dismissedFlagsCache[proj.id]) {
          const dismissedAt = new Date(_dismissedFlagsCache[proj.id].dismissedAt).getTime();
          const lastActivity = getCardLastActivity(proj.id);
          if (lastActivity > dismissedAt) {
            // Card has activity newer than dismissal — un-dismiss by showing a synthetic flag
            cardFlag = { type: 'stale', message: 'Card has new activity since last review' };
          }
        }
        const ambientMode = localStorage.getItem('capAmbientMode') || 'all';
        const showFlag = cardFlag && !cardFlag.dismissed && ambientMode !== 'off'
          && (ambientMode === 'all' || (ambientMode === 'mine' && (
            (proj.owner && proj.owner === (localStorage.getItem(SESSION_USER_KEY) || ''))
            || (proj.contacts || []).some(c => c.name === (localStorage.getItem(SESSION_USER_KEY) || ''))
          )));
        const flagDotHtml = showFlag
          ? `<span class="card-flag-dot" data-card-id="${proj.id}" title="${e(cardFlag.message)}">\u25cf</span>`
          : '';

        const isExpanded = expandedCards.has(proj.id);

        card.innerHTML = `
            ${capBadgeHtml}
            <div class="card-summary" title="Click to expand" role="button" aria-label="Open card">
              <div class="card-top-row">
                <span class="card-drag-handle" title="Drag to reorder">\u283f</span>
                <span class="card-chevron" aria-hidden="true"></span>
                <span class="card-name">${e(proj.name)}</span>${flagDotHtml}
                <span class="card-actions">
                  <button class="card-detail-btn" title="Open card detail">✪</button>
                  <button class="card-duplicate-btn" title="Duplicate card">⧉</button>
                  <button class="card-archive-icon-btn" title="${archived ? 'Unarchive' : 'Archive'}" aria-label="${archived ? 'Unarchive card' : 'Archive card'}">${archived ? '\ud83d\udcc2' : '\ud83d\udcc1'}</button>
                  ${isParent
                    ? `<button class="card-nested-indicator" title="${isCurrentTarget ? 'Collapse sub-board' : 'Open sub-board'}">${isCurrentTarget ? `${childCount > 7 ? '>7' : childCount} \u25be` : `\u25b6 ${childCount > 7 ? '>7' : childCount}`}</button>`
                    : `<button class="card-nest-btn" title="Create sub-board">\u25b6</button>`}
                </span>
              </div>
              ${!isExpanded ? (datePill
                ? `<div class="card-date-row">${datePill}</div>`
                : `<div class="card-date-row"><button class="set-date-btn" data-proj-id="${proj.id}" title="Set start and end dates">\ud83d\udcc5 Set dates</button></div>`) : ''}
              <div class="card-meta">
                <span class="card-next">${metaNext}</span>
                ${metaRight ? `<span class="${metaRightClass}">${metaRight}</span>` : ''}
              </div>
              ${lastDoneHtml}
              ${isParent && !isCurrentTarget && activeChildren.length > 0 ? `
              <div class="ctx-children-list card-children-collapsed">
                ${activeChildren.map(c => {
                  const cs = normalizeStatus(c.status || '');
                  const csStyle = (cs && cs !== 'Active') ? getStatusStyle(cs) : '';
                  return `<div class="ctx-child-item"><span class="ctx-child-name">${e(c.name)}</span>${(cs && cs !== 'Active') ? `<span class="ctx-status-pill" style="${csStyle}">${cs}</span>` : ''}</div>`;
                }).join('')}
              </div>` : ''}
            </div>
            <div class="card-tasks">
              ${(() => {
                const sched = getCardSchedule(proj);
                if (!sched) return "";
                const pill = formatSchedulePill(proj);
                return pill ? `<div class="card-section card-schedule-section">${pill}</div>` : "";
              })()}
              <div class="card-section card-status-section">
                <div class="status-inline-row">
                  <span class="section-label">Status</span>
                  <div class="status-chip-wrap">
                    ${(() => {
                      const chipStatus = normalizeStatus(status || 'Active');
                      return `<button class="status-chip" data-status="${chipStatus}" aria-label="Change status"><span class="status-dot" style="background:${(STATUS_COLORS[chipStatus] || STATUS_COLORS['Active']).dot}"></span>${chipStatus === 'Done' ? '&#10003;' : ''}</button>
                    <div class="status-dropdown hidden">
                      ${STATUS_OPTIONS.map(o => `<button class="status-option${chipStatus === o ? ' active' : ''}" data-value="${o}"><span class="status-dot" style="background:${STATUS_COLORS[o].dot}"></span>${o}</button>`).join('')}
                    </div>`;
                    })()}
                  </div>
                </div>
              </div>
              ${(() => {
                const cardArtifactKey = "card:" + proj.id;
                const cardArtifact = loadOverrides().delegateArtifacts?.[cardArtifactKey] || null;
                if (!cardArtifact || isArtifactSnoozed(cardArtifactKey)) return '';
                // Revising state — show spinner banner, no actions
                if (cardArtifact.revising) {
                  return `<div class="card-artifact-banner card-artifact-banner-revising">
                    <div class="artifact-header-row">
                      <span class="artifact-icon artifact-revising-spin">\u21bb</span>
                      <span class="artifact-header-text">${e(AI_NAME)} is revising\u2026</span>
                    </div>
                    ${cardArtifact.revisingNote ? `<div class="artifact-revising-note">\u201c${e(cardArtifact.revisingNote)}\u201d</div>` : ''}
                  </div>`;
                }
                const isEmailDraft = cardArtifact.type === 'email_draft';
                const cardArtHeader = isEmailDraft ? '\ud83d\udce7 Email draft ready \u2014 review before sending'
                  : cardArtifact.type === 'research' ? '\ud83d\udd0d Research complete'
                  : cardArtifact.type === 'document' ? '\ud83d\udcc4 Draft ready'
                  : `\u25c8 ${e(AI_NAME)}: Result ready`;
                const cardConfHtml = cardArtifact.confidence
                  ? `<span class="artifact-confidence artifact-confidence-${cardArtifact.confidence}">${confidenceGlyph(cardArtifact.confidence)} ${e(cardArtifact.confidence)}</span>` : '';
                const cardProvHtml = cardArtifact.provenance
                  ? `<span class="artifact-provenance-inline">${e(cardArtifact.provenance)}</span>` : '';
                return `<div class="card-artifact-banner">
                  <div class="artifact-header-row">
                    <span class="artifact-icon">\u25c8</span>
                    <span class="artifact-header-text">${cardArtHeader}</span>
                    ${cardConfHtml} ${cardProvHtml}
                  </div>
                  <div class="artifact-preview">${e((cardArtifact.content || '').slice(0, 200))}${(cardArtifact.content || '').length > 200 ? '\u2026' : ''}</div>
                  <div class="artifact-actions">
                    <button class="delegate-btn-approve" data-artifact-task-id="${cardArtifactKey}" data-delegate-id="${cardArtifact.delegateId}" data-artifact-type="${e(cardArtifact.type)}">${isEmailDraft ? '\u2713 Approve & Send' : '\u2713 Approve'}</button>
                    <button class="delegate-btn-view" data-artifact-task-id="${cardArtifactKey}">View full</button>
                    <button class="delegate-btn-edit" data-artifact-task-id="${cardArtifactKey}">Edit</button>
                    <button class="delegate-btn-snooze" data-artifact-task-id="${cardArtifactKey}">\u23f0 Snooze</button>
                    <button class="delegate-btn-discard" data-artifact-task-id="${cardArtifactKey}" data-delegate-id="${cardArtifact.delegateId}">Discard</button>
                  </div>
                </div>`;
              })()}
              <div class="card-section">
                <div class="section-header"><span class="section-label">Tasks</span>${isParent && tasks.filter(t => !t.done).length > 0 ? `<button class="convert-tasks-btn" data-card-id="${proj.id}" title="Move open tasks to sub-cards">↗ Move to sub-cards</button>` : ''}</div>
                <ul class="card-tasks-list" data-project-id="${proj.id}">
                  ${tasks.map(t => {
                    const { context: tCtx, displayText: tDisplay } = parseContext(t.text);
                    const isDelegate = tCtx === 'delegate';
                    const ctxPill = tCtx ? `<span class="task-ctx-pill${tCtx === 'waiting' ? ' task-ctx-waiting' : ''}${isDelegate ? ' task-ctx-delegate' : ''}" title="Context: @${tCtx}">@${e(tCtx)}</span>` : '';
                    // Artifact card — shown when Cap has returned a result for this @delegate task
                    const artifact = isDelegate && !t.done && !isArtifactSnoozed(t.id) ? (loadOverrides().delegateArtifacts?.[t.id] || null) : null;
                    const isEmailDraft = artifact?.type === 'email_draft';
                    const artifactHeader = artifact
                      ? (isEmailDraft ? '\ud83d\udce7 Email draft ready \u2014 review before sending'
                        : artifact.type === 'research' ? '\ud83d\udd0d Research complete'
                        : artifact.type === 'document' ? '\ud83d\udcc4 Draft ready'
                        : '\u25c8 Cap: Result ready')
                      : '';
                    const provenanceHtml = artifact && artifact.provenance
                      ? `<div class="artifact-provenance">${e(artifact.provenance)}</div>` : '';
                    const confidenceHtml = artifact && artifact.confidence
                      ? `<span class="artifact-confidence artifact-confidence-${artifact.confidence}">${confidenceGlyph(artifact.confidence)} ${e(artifact.confidence)}</span>` : '';
                    const isEmptyResult = artifact && artifact.empty === true;
                    const isRevising = artifact && artifact.revising === true;
                    const artifactHtml = artifact ? (isRevising ? `
                      <div class="delegate-artifact delegate-artifact-revising" data-task-id="${t.id}">
                        <div class="delegate-artifact-header delegate-artifact-header-revising">\u21bb ${e(AI_NAME)} is revising\u2026</div>
                        ${artifact.revisingNote ? `<div class="artifact-revising-note">\u201c${e(artifact.revisingNote)}\u201d</div>` : ''}
                      </div>` : isEmptyResult ? `
                      <div class="delegate-artifact delegate-artifact-empty" data-task-id="${t.id}">
                        <div class="delegate-artifact-header delegate-artifact-header-empty">\u25CB ${e(AI_NAME)} searched but found nothing for this task.</div>
                        ${artifact.reason ? `<div class="artifact-provenance">${e(artifact.reason)}</div>` : ''}
                        <div class="delegate-artifact-actions">
                          <button class="delegate-btn-retry" data-task-id="${t.id}" data-card-id="${proj.id}" data-card-name="${e(proj.name)}" data-task-text="${e(t.text)}">Try again</button>
                          <button class="delegate-btn-discard" data-artifact-task-id="${t.id}" data-delegate-id="${artifact.delegateId || ''}" title="Discard">Discard</button>
                        </div>
                      </div>` : `
                      <div class="delegate-artifact${isEmailDraft ? ' delegate-artifact-email' : ''}" data-task-id="${t.id}">
                        <div class="delegate-artifact-header">${artifactHeader}${confidenceHtml}</div>
                        ${provenanceHtml}
                        ${artifact.subject ? `<div style="font-size:0.75rem;color:var(--text-dim);margin-bottom:4px">Subject: ${e(artifact.subject)}</div>` : ''}
                        <div class="delegate-artifact-preview">${e(artifact.content)}</div>
                        <div class="delegate-artifact-actions">
                          <button class="delegate-btn-approve" data-artifact-task-id="${t.id}" data-artifact-type="${e(artifact.type)}" title="${isEmailDraft ? 'Approve &amp; queue for sending' : 'Approve &amp; mark done'}">${isEmailDraft ? '\u2713 Approve & Send' : '\u2713 Approve'}</button>
                          ${isEmailDraft ? `<button class="delegate-btn-save-only" data-artifact-task-id="${t.id}" title="Save draft without sending">Save only</button>` : ''}
                          <button class="delegate-btn-edit" data-artifact-task-id="${t.id}" title="Edit content">Edit</button>
                          <button class="delegate-btn-view" data-artifact-task-id="${t.id}" title="View full content">View full</button>
                          <button class="delegate-btn-snooze" data-artifact-task-id="${t.id}" title="Snooze">\u23f0 Snooze</button>
                          <button class="delegate-btn-discard" data-artifact-task-id="${t.id}" title="Discard">Discard</button>
                        </div>
                      </div>`) : '';
                    const _dqc = getDelegateQueueCache();
                    const isDelegatePending = isDelegate && !t.done && !artifact && Array.isArray(_dqc)
                      && _dqc.some(q => (q.taskId === t.id) && (q.status === 'pending' || q.status === 'in_progress'));
                    const pendingIndicatorHtml = isDelegatePending
                      ? `<div class="delegate-pending-indicator">\u25C8 ${e(AI_NAME)} is on it\u2026</div>` : '';
                    return `
                    <li class="task-item ${t.done ? 'done' : ''}${tCtx === 'waiting' ? ' task-is-waiting' : ''}${isDelegate ? ' task-is-delegate' : ''}${isDelegatePending ? ' delegate-pending' : ''}" data-task-id="${t.id}">
                      <div class="task-main-row">
                        <span class="task-handle" title="Drag to reorder">\u283f</span>
                        <input type="checkbox" ${t.done ? 'checked' : ''} data-task-id="${t.id}">
                        ${t.ripe ? '<span class="task-ripe-indicator" title="Cue">\ud83d\udd37</span>' : ''}
                        <span class="task-text" data-task-id="${t.id}" title="Click to edit">${e(tDisplay)}</span>
                        ${t.done && t.completedAt ? `<span class="task-done-date">${formatTaskDate(t.completedAt)}</span>` : t.dueDate ? formatTaskDuePill(t.dueDate) : `<span class="task-set-date" data-task-id="${t.id}" title="Set due date">\ud83d\udcc5</span>`}
                        <button class="task-delete-btn" data-task-id="${t.id}" title="Delete task">\u00d7</button>
                      </div>
                      ${pendingIndicatorHtml}
                      <div class="task-meta-row${(t.owner || ctxPill) ? ' has-content' : ''}">
                        ${ctxPill}
                        <span class="task-owner-pill${t.owner ? '' : ' task-owner-empty'}" data-task-id="${t.id}" title="${t.owner ? 'Edit owner' : 'Assign owner'}">${t.owner ? '@' + e(t.owner) : '+ assign'}</span>
                      </div>
                      ${artifactHtml}
                    </li>`;
                  }).join('')}
                </ul>
                ${tasks.length === 0 && (proj.outcome || proj.scope) && !_suggestionCache[proj.id]
                  ? `<div class="suggest-prompt" data-card-id="${proj.id}">
                      <span class="suggest-text">No next actions yet.</span>
                      <button class="suggest-btn" data-card-id="${proj.id}">${e(AI_NAME)} has a suggestion \u2192</button>
                    </div>`
                  : (_suggestionCache[proj.id]?.status === 'loading'
                    ? `<div class="suggest-prompt" data-card-id="${proj.id}"><span class="suggest-loading">${e(AI_NAME)} is thinking\u2026</span></div>`
                    : (_suggestionCache[proj.id]?.status === 'ready'
                      ? `<div class="suggest-ghost-task" data-card-id="${proj.id}">
                          <span class="suggest-ghost-text">${e(_suggestionCache[proj.id].suggestion)}</span>
                          <button class="suggest-add-btn" data-card-id="${proj.id}" data-suggestion="${e(_suggestionCache[proj.id].suggestion)}">+ Add</button>
                          <button class="suggest-skip-btn" data-card-id="${proj.id}">Skip</button>
                        </div>`
                      : ''))}
                <div class="add-task-row">
                  <textarea class="add-task-input" placeholder="Next physical step\u2026" data-project-id="${proj.id}" rows="1"></textarea>
                  <button class="ripe-toggle" data-project-id="${proj.id}" title="Mark as Cue (quick action)">\ud83d\udd37</button>
                  <button class="add-task-btn" data-project-id="${proj.id}">Add</button>
                </div>
              </div>
              ${!isParent ? `<button class="card-delegate-cta" data-project-id="${proj.id}" title="Hand something off to ${e(AI_NAME)}">◈ Delegate to ${e(AI_NAME)}</button>` : ''}
              ${(() => {
                const hist = loadOverrides().delegateHistory?.[proj.id];
                if (!hist || !hist.length) return '';
                return `<div class="cap-history-section">
                  <button class="cap-history-toggle">${e(AI_NAME)} history <span class="cap-history-count">(${hist.length})</span></button>
                  <div class="cap-history-list hidden">${hist.slice().reverse().map(h => {
                    const icon = h.action === 'approved' ? '\u2713' : '\u2717';
                    const label = h.action === 'approved' ? 'Approved' : 'Discarded';
                    const typeLabel = h.type === 'email_draft' ? 'email draft' : h.type || 'result';
                    const d = new Date(h.at);
                    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    const timeStr = months[d.getMonth()] + ' ' + d.getDate() + ' ' + d.getHours() + ':' + String(d.getMinutes()).padStart(2,'0');
                    return `<div class="cap-history-entry cap-history-${h.action}"><span class="cap-history-icon">${icon}</span> ${label} ${e(typeLabel)} \u2014 ${timeStr}</div>`;
                  }).join('')}</div>
                </div>`;
              })()}
              <div class="section-pills">
                <button class="sec-pill${!showAll && openSec==='outcome'?' sec-pill-active':''}" data-sec="outcome" title="Scope \u2014 What does success look like? Define the boundaries and goal for this project.">${hasOutcome?'Scope<span class="sec-dot"></span>':'Scope<span class="sec-pill-add">+</span>'}</button>
                <button class="sec-pill${!showAll && openSec==='waitingfor'?' sec-pill-active':''}" data-sec="waitingfor" title="Blocked \u2014 Record who or what you\u2019re waiting on before this can move forward.">${hasWaitingFor?'Blocked<span class="sec-dot"></span>':'Blocked<span class="sec-pill-add">+</span>'}</button>
                <button class="sec-pill${!showAll && openSec==='contacts'?' sec-pill-active':''}" data-sec="contacts" title="People \u2014 Add the key people involved in this project. ${e(AI_NAME)} can monitor their emails for updates.">${hasContacts?'People<span class="sec-dot"></span>':'People<span class="sec-pill-add">+</span>'}</button>
                <button class="sec-pill${!showAll && openSec==='comms'?' sec-pill-active':''}" data-sec="comms" title="Emails \u2014 ${e(AI_NAME)} tracks emails from your contacts on this project and surfaces them here.">${hasComms?'Emails<span class="sec-dot"></span>':'Emails<span class="sec-pill-add">+</span>'}</button>
                <button class="sec-pill${!showAll && openSec==='progressnotes'?' sec-pill-active':''}" data-sec="progressnotes" title="Log \u2014 Timestamped notes and updates you\u2019ve added as this project moves forward.">${hasProgressNotes?'Log<span class="sec-dot"></span>':'Log<span class="sec-pill-add">+</span>'}</button>
              </div>
              ${showAll ? `
              <div class="section-all-expanded">
                <div class="section-all-block">
                  <div class="section-all-label">Scope</div>
                  <textarea class="outcome-field" data-proj-id="${proj.id}" placeholder="What does success look like? Define the goal or scope\u2026">${e(proj.outcome || '')}</textarea>
                  ${!isParent ? `<span class="scope-delegate-hint">Tip: start with @delegate to hand this off to ${e(AI_NAME)}</span>` : ''}
                </div>
                <div class="section-all-block">
                  <div class="section-all-label">Blocked</div>
                  <textarea class="waiting-for-field" data-proj-id="${proj.id}" placeholder="Who or what needs to happen before this can move forward?">${e(waitingFor)}</textarea>
                </div>
                <div class="section-all-block">
                  <div class="section-all-label">People</div>
                  <div class="contacts-list">
                    ${contacts.length === 0
                      ? `<div class="section-empty-rich"><span class="section-empty-icon">\ud83d\udc64</span><span>No people added yet.</span></div>`
                      : contacts.map((c, i) => `<div class="contact-item"><div class="contact-info"><span class="contact-name">${e(c.name)}</span><span class="contact-email">${e(c.email)}</span></div><button class="contact-remove" data-index="${i}" title="Remove">\u00d7</button></div>`).join('')}
                  </div>
                  <div class="add-contact-form hidden">
                    <input type="text" class="contact-name-input" placeholder="Name">
                    <input type="email" class="contact-email-input" placeholder="Email">
                    <div class="contact-form-actions">
                      <button class="contact-save-btn">Add</button>
                      <button class="contact-cancel-btn">Cancel</button>
                    </div>
                  </div>
                  <button class="add-contact-btn section-action-btn">+ Add person</button>
                </div>
                <div class="section-all-block">
                  <div class="section-all-label">Emails</div>
                  <div class="comms-list">
                    ${comms.length === 0
                      ? `<div class="section-empty-rich"><span class="section-empty-icon">\ud83d\udce7</span><span>No emails yet.</span></div>`
                      : [...comms].reverse().map(c => `<div class="comms-entry"><div class="comms-meta"><span class="comms-date">${formatShortDate(c.date)}</span>${c.source==='cap'?`<span class="comms-badge">${e(AI_NAME)}</span>`:''}${c.messageId?`<a class="comms-link" href="https://mail.google.com/mail/u/0/#all/${c.messageId}" target="_blank">\u2197</a>`:''}</div><div class="comms-text">${e(c.text)}</div></div>`).join('')}
                  </div>
                </div>
                <div class="section-all-block">
                  <div class="section-all-label">Log</div>
                  <div class="progress-notes-input-row">
                    <input type="text" class="progress-note-input" placeholder="Add a timestamped note or update\u2026" data-proj-id="${proj.id}" maxlength="200">
                    <button class="progress-note-add-btn" data-proj-id="${proj.id}">Add</button>
                  </div>
                  <div class="progress-notes-list">
                    ${progressNotes.length === 0 ? '<div class="section-empty-rich"><span class="section-empty-icon">\ud83d\udcdd</span><span>Nothing logged yet.</span></div>' : ''}
                    ${progressNotes.slice().reverse().map(n => `<div class="progress-note-item"><span class="progress-note-text">${linkify(n.text)}</span><span class="progress-note-time">${formatRelativeTime(n.timestamp)}</span></div>`).join('')}
                  </div>
                </div>
              </div>` : `
              <div class="section-pill-body${openSec ? '' : ' hidden'}">
                ${openSec === 'outcome' ? `
                  <textarea class="outcome-field" data-proj-id="${proj.id}" placeholder="What does success look like? Define the goal or scope\u2026">${e(proj.outcome || '')}</textarea>
                  ${!isParent ? `<span class="scope-delegate-hint">Tip: start with @delegate to hand this off to ${e(AI_NAME)}</span>` : ''}` : ''}
                ${openSec === 'waitingfor' ? `
                  <textarea class="waiting-for-field" data-proj-id="${proj.id}" placeholder="Who or what needs to happen before this can move forward?">${e(waitingFor)}</textarea>` : ''}
                ${openSec === 'contacts' ? `
                  <div class="contacts-list">
                    ${contacts.length === 0
                      ? `<div class="section-empty-rich"><span class="section-empty-icon">\ud83d\udc64</span><span>No people added yet. Add contacts and ${e(AI_NAME)} can monitor their emails for updates related to this project.</span></div>`
                      : contacts.map((c, i) => `<div class="contact-item"><div class="contact-info"><span class="contact-name">${e(c.name)}</span><span class="contact-email">${e(c.email)}</span></div><button class="contact-remove" data-index="${i}" title="Remove">\u00d7</button></div>`).join('')}
                  </div>
                  <div class="add-contact-form hidden">
                    <input type="text" class="contact-name-input" placeholder="Name">
                    <input type="email" class="contact-email-input" placeholder="Email">
                    <div class="contact-form-actions">
                      <button class="contact-save-btn">Add</button>
                      <button class="contact-cancel-btn">Cancel</button>
                    </div>
                  </div>
                  <button class="add-contact-btn section-action-btn">+ Add person</button>` : ''}
                ${openSec === 'comms' ? `
                  <div class="comms-list">
                    ${comms.length === 0
                      ? `<div class="section-empty-rich"><span class="section-empty-icon">\ud83d\udce7</span><span>No emails yet. Add people in the \u201cPeople\u201d tab and ${e(AI_NAME)} will surface relevant emails here automatically.</span></div>`
                      : [...comms].reverse().map(c => `<div class="comms-entry"><div class="comms-meta"><span class="comms-date">${formatShortDate(c.date)}</span>${c.source==='cap'?`<span class="comms-badge">${e(AI_NAME)}</span>`:''}${c.messageId?`<a class="comms-link" href="https://mail.google.com/mail/u/0/#all/${c.messageId}" target="_blank">\u2197</a>`:''}</div><div class="comms-text">${e(c.text)}</div></div>`).join('')}
                  </div>` : ''}
                ${openSec === 'progressnotes' ? `
                  <div class="progress-notes-input-row">
                    <input type="text" class="progress-note-input" placeholder="Add a timestamped note or update\u2026" data-proj-id="${proj.id}" maxlength="200">
                    <button class="progress-note-add-btn" data-proj-id="${proj.id}">Add</button>
                  </div>
                  <div class="progress-notes-list">
                    ${progressNotes.length === 0 ? '<div class="section-empty-rich"><span class="section-empty-icon">\ud83d\udcdd</span><span>Nothing logged yet. Notes are timestamped and build a running history of this project.</span></div>' : ''}
                    ${progressNotes.slice().reverse().map(n => `<div class="progress-note-item"><span class="progress-note-text">${linkify(n.text)}</span><span class="progress-note-time">${formatRelativeTime(n.timestamp)}</span></div>`).join('')}
                  </div>` : ''}
              </div>`}
              <div class="card-show-all-row">
                <button class="card-show-all-toggle" data-proj-id="${proj.id}" title="${showAll ? 'Collapse to tab view' : 'Show all sections at once'}">${showAll ? 'Show less' : 'Show all sections'}</button>
              </div>
            </div>
          `;

          attachCardRename(card, proj);

          // ── Flag dot dismiss on click ──
          const flagDotEl = card.querySelector('.card-flag-dot');
          if (flagDotEl) {
            flagDotEl.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const cId = flagDotEl.dataset.cardId;
              fetch(`/api/v1/flags/${encodeURIComponent(cId)}`, {
                method: 'DELETE',
                headers: getAuthHeader(),
              }).catch(err => console.warn('[flags] dismiss failed:', err));
              delete _cardFlagsCache[cId];
              renderFromState();
            });
          }

          // ── Card due date pill click ──
          const datePillEl = card.querySelector('.date-pill[data-proj-id]');
          if (datePillEl) {
            datePillEl.addEventListener('click', (e) => {
              e.stopPropagation();
              openDateRangePopover(proj.id, proj, datePillEl);
            });
          }

          // ── "+ Set due date" link on cards with no due date ──
          const setDateEl = card.querySelector('.card-set-date-btn');
          if (setDateEl) {
            setDateEl.addEventListener('click', (e) => {
              e.stopPropagation();
              openDateRangePopover(proj.id, proj, setDateEl);
            });
          }

          // ── "Set dates" button on dateless cards ──
          const setDateBtn = card.querySelector('.set-date-btn[data-proj-id]');
          if (setDateBtn) {
            setDateBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              openDateRangePopover(proj.id, proj, setDateBtn);
            });
          }


          if (expandedCards.has(proj.id)) {
            card.classList.add('expanded');
            initTaskSortable(card.querySelector('.card-tasks-list'));
          }

          // ── Duplicate card ──
          card.querySelector('.card-duplicate-btn').addEventListener('click', (e) => {
            e.stopPropagation();
            duplicateCard(proj, col.id);
          });

          // ── Archive icon on card face ──
          card.querySelector('.card-archive-icon-btn').addEventListener('click', (e) => {
            e.stopPropagation();
            toggleArchive(proj.id);
          });

          // ── Detail button → opens Super Panel ──
          card.querySelector('.card-detail-btn').addEventListener('click', (e) => {
            e.stopPropagation();
            openWorkspace(proj.id);
          });

          card.querySelector('.card-summary').addEventListener('click', (e) => {
            if (e.target.closest('.card-drag-handle')) return;
            if (e.target.closest('.card-nested-indicator')) return;
            if (e.target.closest('.card-nest-btn')) return;
            if (e.target.closest('.card-archive-icon-btn')) return;
            if (e.target.closest('.card-detail-btn')) return;
            if (e.target.closest('.status-chip-wrap')) return;
            const wasExpanded = card.classList.contains('expanded');
            card.classList.toggle('expanded');
            if (wasExpanded) expandedCards.delete(proj.id);
            else { expandedCards.add(proj.id); initTaskSortable(card.querySelector('.card-tasks-list')); }
          });

          // ── Status chip toggle ──
          const statusChip = card.querySelector('.status-chip');
          const statusDropdown = card.querySelector('.status-dropdown');
          statusChip.addEventListener('click', (e) => {
            e.stopPropagation();
            document.querySelectorAll('.status-dropdown:not(.hidden)').forEach(d => d.classList.add('hidden'));
            statusDropdown.classList.toggle('hidden');
          });
          statusDropdown.querySelectorAll('.status-option').forEach(opt => {
            opt.addEventListener('click', (e) => {
              e.stopPropagation();
              saveStatus(proj.id, opt.dataset.value);
              statusDropdown.classList.add('hidden');
              renderFromState();
            });
          });
          document.addEventListener('click', () => statusDropdown.classList.add('hidden'), { once: true });

          // Contacts section — only wired when section is open
          const addContactBtn = card.querySelector('.add-contact-btn');
          if (addContactBtn) {
            const addContactForm = card.querySelector('.add-contact-form');
            const contactsList = card.querySelector('.contacts-list');
            const contactNameInput = card.querySelector('.contact-name-input');
            const contactEmailInput = card.querySelector('.contact-email-input');
            const contactSaveBtn = card.querySelector('.contact-save-btn');
            const contactCancelBtn = card.querySelector('.contact-cancel-btn');
            addContactBtn.addEventListener('click', (e) => { e.stopPropagation(); addContactForm.classList.remove('hidden'); contactNameInput.value=''; contactEmailInput.value=''; contactNameInput.focus(); });
            contactNameInput.addEventListener('click', (e) => e.stopPropagation());
            contactEmailInput.addEventListener('click', (e) => e.stopPropagation());
            contactSaveBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              const name = contactNameInput.value.trim(); const email = contactEmailInput.value.trim();
              if (!name && !email) return;
              const ov2 = loadOverrides(); if (!ov2.cardContacts) ov2.cardContacts = {};
              const current = ov2.cardContacts[proj.id] || [...contacts];
              current.push({ name, email }); ov2.cardContacts[proj.id] = current;
              saveOverrides(ov2); renderFromState();
            });
            contactCancelBtn.addEventListener('click', (e) => { e.stopPropagation(); addContactForm.classList.add('hidden'); });
            contactsList.querySelectorAll('.contact-remove').forEach(btn => {
              btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const idx = parseInt(btn.dataset.index); const ov2 = loadOverrides();
                if (!ov2.cardContacts) ov2.cardContacts = {};
                const current = ov2.cardContacts[proj.id] ? [...ov2.cardContacts[proj.id]] : [...contacts];
                current.splice(idx, 1); ov2.cardContacts[proj.id] = current;
                saveOverrides(ov2); renderFromState();
              });
            });
          }

          card.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.addEventListener('change', (e) => {
              e.stopPropagation();
              const taskId = cb.dataset.taskId; const ov2 = loadOverrides();
              if (!ov2.taskStates) ov2.taskStates = {};
              ov2.taskStates[taskId] = cb.checked;
              if (!ov2.taskCompletedAt) ov2.taskCompletedAt = {};
              if (cb.checked) ov2.taskCompletedAt[taskId] = new Date().toISOString();
              else delete ov2.taskCompletedAt[taskId];
              // Day 10: Log completed task as a Win
              if (cb.checked) {
                if (!ov2.completedTasks) ov2.completedTasks = [];
                const taskEl = cb.closest('.task-item');
                const taskText = taskEl ? taskEl.querySelector('.task-text')?.textContent || '' : '';
                ov2.completedTasks.push({ taskId, cardId: proj.id, cardName: proj.name, text: taskText, completedAt: new Date().toISOString() });
                if (ov2.completedTasks.length > 50) ov2.completedTasks = ov2.completedTasks.slice(-50);
              }
              saveOverrides(ov2);
              if (cb.checked) {
                // Day 3: completion micro-animation before re-render
                const li = cb.closest('.task-item');
                if (li) {
                  li.classList.add('task-completing');
                  // Day 10: "Win logged" micro-text
                  const winMsg = document.createElement('span');
                  winMsg.className = 'win-logged-msg';
                  winMsg.textContent = 'Win logged';
                  li.appendChild(winMsg);
                  setTimeout(() => winMsg.remove(), 1500);
                }
                // Day 14: refresh badge without opening wins panel
                if (typeof renderWins === 'function') renderWins();
                // Day 16: check transition when task completes
                if (typeof checkIntentionTransition === 'function') checkIntentionTransition();
                setTimeout(() => renderFromState(), 300);
              } else {
                renderFromState();
              }
            });
            cb.addEventListener('click', (e) => e.stopPropagation());
          });

          // ── Suggestion prompt handlers ──
          const suggestBtn = card.querySelector('.suggest-btn');
          if (suggestBtn) {
            suggestBtn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const cId = suggestBtn.dataset.cardId;
              // Cancel any existing suggest poll (only one card at a time)
              if (_activeSuggestPollTimer) {
                clearInterval(_activeSuggestPollTimer);
                setActiveSuggestPollTimer(null);
              }
              if (_activeSuggestPollTimeout) {
                clearTimeout(_activeSuggestPollTimeout);
                setActiveSuggestPollTimeout(null);
              }
              if (_activeSuggestPollCardId && _activeSuggestPollCardId !== cId) {
                delete _suggestionCache[_activeSuggestPollCardId];
              }
              setActiveSuggestPollCardId(cId);
              _suggestionCache[cId] = { status: 'loading' };
              renderFromState();
              fetch('/api/v1/suggest-next-action', {
                method: 'POST',
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  cardId: cId,
                  cardName: proj.name || '',
                  cardScope: proj.outcome || proj.scope || '',
                  cardNotes: (proj.comms || []).map(c => c.text).join('\n'),
                  existingTasks: (proj.tasks || []).map(t => t.text),
                }),
              }).then(() => {
                // Poll every 5 seconds for result (reduced from 3s to stay under rate limit)
                setActiveSuggestPollTimer(setInterval(async () => {
                  try {
                    const r = await fetch(`/api/v1/suggest-next-action?cardId=${encodeURIComponent(cId)}`, { headers: getAuthHeader() });
                    if (!r.ok) return;
                    const data = await r.json();
                    if (data.status === 'ready') {
                      clearInterval(_activeSuggestPollTimer);
                      setActiveSuggestPollTimer(null);
                      setActiveSuggestPollCardId(null);
                      _suggestionCache[cId] = { status: 'ready', suggestion: data.suggestion };
                      renderFromState();
                    } else if (data.status === 'failed' || data.status === 'none') {
                      clearInterval(_activeSuggestPollTimer);
                      setActiveSuggestPollTimer(null);
                      setActiveSuggestPollCardId(null);
                      delete _suggestionCache[cId];
                      renderFromState();
                    }
                  } catch { /* retry on next interval */ }
                }, 5000));
                // Stop polling after 2 minutes
                setActiveSuggestPollTimeout(setTimeout(() => {
                  clearInterval(_activeSuggestPollTimer);
                  setActiveSuggestPollTimer(null);
                  setActiveSuggestPollCardId(null);
                  if (_suggestionCache[cId]?.status === 'loading') { delete _suggestionCache[cId]; renderFromState(); }
                }, 120000));
              }).catch(err => {
                console.warn('[suggest] enqueue failed:', err);
                setActiveSuggestPollCardId(null);
                delete _suggestionCache[cId];
                renderFromState();
              });
            });
          }
          const suggestAddBtn = card.querySelector('.suggest-add-btn');
          if (suggestAddBtn) {
            suggestAddBtn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const cId = suggestAddBtn.dataset.cardId;
              const text = suggestAddBtn.dataset.suggestion;
              if (text) {
                const taskId = cId + '-custom-' + Date.now();
                const ov2 = loadOverrides();
                if (!ov2.addedTasks) ov2.addedTasks = {};
                if (!ov2.addedTasks[cId]) ov2.addedTasks[cId] = [];
                ov2.addedTasks[cId].push({ id: taskId, text, done: false });
                saveOverrides(ov2);
              }
              delete _suggestionCache[cId];
              renderFromState();
            });
          }
          const suggestSkipBtn = card.querySelector('.suggest-skip-btn');
          if (suggestSkipBtn) {
            suggestSkipBtn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              delete _suggestionCache[suggestSkipBtn.dataset.cardId];
              renderFromState();
            });
          }

          // ── Convert tasks → sub-cards (for parent cards with orphaned tasks) ──
          const convertTasksBtn = card.querySelector('.convert-tasks-btn');
          if (convertTasksBtn) {
            convertTasksBtn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const ov2 = loadOverrides();
              const openTasks = tasks.filter(t => !t.done);
              const children = openTasks.map((t, i) => ({
                id: proj.id + '-child-' + Date.now() + '-' + i,
                name: t.text,
                color: PRESET_COLORS[i % PRESET_COLORS.length],
                createdAt: new Date().toISOString().split('T')[0],
                columnId: 'todo',
                tasks: [], status: '', contacts: [], comms: []
              }));
              if (!ov2.addedChildren) ov2.addedChildren = {};
              if (!ov2.addedChildren[proj.id]) ov2.addedChildren[proj.id] = [];
              ov2.addedChildren[proj.id].push(...children);
              // Remove converted tasks from addedTasks
              if (ov2.addedTasks && ov2.addedTasks[proj.id]) {
                const openIds = new Set(openTasks.map(t => t.id));
                ov2.addedTasks[proj.id] = ov2.addedTasks[proj.id].filter(t => !openIds.has(t.id));
              }
              saveOverrides(ov2);
              drillInto(proj.id, proj.name);
            });
          }

          const addInput = card.querySelector('.add-task-input');
          const addBtn = card.querySelector('.add-task-btn');
          const ripeToggle = card.querySelector('.ripe-toggle');
          let _addTaskRipe = false;

          // Wire ripe toggle
          if (ripeToggle) {
            ripeToggle.style.opacity = '0.3';
            ripeToggle.addEventListener('click', (ev) => {
              ev.stopPropagation();
              _addTaskRipe = !_addTaskRipe;
              ripeToggle.style.opacity = _addTaskRipe ? '1' : '0.3';
            });
          }

          // ── Hint element for nest-mode (80+ chars → sub-card) ──
          const taskNestHint = document.createElement('div');
          taskNestHint.className = 'task-nest-hint';
          taskNestHint.textContent = '↳ Will become a sub-card — press Enter to create';
          addInput.closest('.add-task-row').appendChild(taskNestHint);

          function addTask() {
            const text = addInput.value.trim(); if (!text) return;

            // ── Auto-nest: 80+ chars → convert to sub-card ──
            if (text.length >= 80) {
              const ov2 = loadOverrides();
              // If leaf card, convert to parent first
              if (!isParentCard(proj)) {
                if (!ov2.convertedToParent) ov2.convertedToParent = {};
                ov2.convertedToParent[proj.id] = { subBoardColumns: JSON.parse(JSON.stringify(DEFAULT_SUB_COLUMNS)) };
                // Migrate existing tasks to child cards
                const existingTasks = proj.tasks || [];
                if (existingTasks.length > 0) {
                  if (!ov2.addedChildren) ov2.addedChildren = {};
                  if (!ov2.addedChildren[proj.id]) ov2.addedChildren[proj.id] = [];
                  const taskChildren = existingTasks.map((t, i) => ({
                    id: proj.id + '-migrated-' + Date.now() + '-' + i,
                    name: t.text,
                    color: PRESET_COLORS[i % PRESET_COLORS.length],
                    createdAt: new Date().toISOString().split('T')[0],
                    columnId: t.done ? 'done' : 'todo',
                    tasks: [], status: t.done ? 'Done' : '', contacts: [], comms: []
                  }));
                  ov2.addedChildren[proj.id].push(...taskChildren);
                }
              }
              // Add the long text as a new child card
              const childId = proj.id + '-nested-' + Date.now();
              if (!ov2.addedChildren) ov2.addedChildren = {};
              if (!ov2.addedChildren[proj.id]) ov2.addedChildren[proj.id] = [];
              ov2.addedChildren[proj.id].push({
                id: childId,
                name: text,
                color: PRESET_COLORS[ov2.addedChildren[proj.id].length % PRESET_COLORS.length],
                createdAt: new Date().toISOString().split('T')[0],
                columnId: 'todo',
                tasks: [], status: '', contacts: [], comms: []
              });
              saveOverrides(ov2);
              addInput.value = '';
              addInput.closest('.add-task-row').classList.remove('expanding', 'nest-mode');
              drillInto(proj.id, proj.name);
              return;
            }

            // ── Normal task add ──
            const hasCueTrigger = _addTaskRipe || /@(?:quick|cue)\b/i.test(text) || /\bquick\b/i.test(text);
            const cleanText = text.replace(/@(?:quick|cue)\b/gi, '').replace(/\bquick\b/gi, '').replace(/\s{2,}/g, ' ').trim();
            const taskId = proj.id + '-custom-' + Date.now();
            const ov2 = loadOverrides();
            if (!ov2.addedTasks) ov2.addedTasks = {};
            if (!ov2.addedTasks[proj.id]) ov2.addedTasks[proj.id] = [];
            ov2.addedTasks[proj.id].push({ id: taskId, text: cleanText, done: false });
            // Save cue flag if toggled or triggered by @quick/@cue/quick
            if (hasCueTrigger) {
              if (!ov2.taskRipe) ov2.taskRipe = {};
              ov2.taskRipe[taskId] = true;
              _addTaskRipe = false;
              if (ripeToggle) ripeToggle.style.opacity = '0.3';
            }
            saveOverrides(ov2);

            // Enqueue @delegate tasks to the AI worker
            if (/@delegate\b/i.test(text)) {
              fetch('/api/v1/delegate', {
                method: 'POST',
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  cardId: proj.id,
                  cardName: proj.name || '',
                  taskId: taskId,
                  taskText: text,
                  cardScope: proj.outcome || proj.scope || '',
                  cardNotes: (proj.comms || []).map(c => c.text).join('\n'),
                }),
              }).catch(e => console.warn('[delegate] enqueue failed:', e.message));
            }

            addInput.value = ''; setPendingTaskFocusId(proj.id);
            addInput.closest('.add-task-row').classList.remove('expanding', 'nest-mode');
            renderFromState();
          }
          addBtn.addEventListener('click', (e) => { e.stopPropagation(); addTask(); });
          addInput.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key==='Enter') { e.preventDefault(); addTask(); } });
          addInput.addEventListener('click', (e) => e.stopPropagation());

          // ── Card-level delegate button ──
          const cardDelegateBtn = card.querySelector('.card-delegate-cta');
          if (cardDelegateBtn) {
            cardDelegateBtn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              openDelegatePrompt(proj);
            });
          }

          // ── @delegate discoverability hint ──
          const delegateHint = document.createElement('span');
          delegateHint.className = 'delegate-hint';
          delegateHint.textContent = `Tip: add @delegate to hand off to ${AI_NAME}`;
          addInput.closest('.add-task-row').appendChild(delegateHint);
          addInput.addEventListener('focus', () => {
            const row = addInput.closest('.add-task-row');
            if (!addInput.value.trim()) row.classList.add('show-delegate-hint');
          });
          addInput.addEventListener('blur', () => {
            addInput.closest('.add-task-row').classList.remove('show-delegate-hint');
          });

          // ── Dynamic expansion visual feedback + auto-resize ──
          addInput.addEventListener('input', () => {
            // Auto-resize textarea as content grows
            addInput.style.height = 'auto';
            addInput.style.height = Math.min(addInput.scrollHeight, 120) + 'px';
            const len = addInput.value.length;
            const val = addInput.value;
            const row = addInput.closest('.add-task-row');
            const hasDelegate = /@delegate\b/i.test(val);
            row.classList.toggle('show-delegate-hint', len === 0 && document.activeElement === addInput);
            row.classList.toggle('expanding', len >= 40 && len < 80);
            row.classList.toggle('nest-mode', len >= 80);
            // Update or remove the hint label
            let hint = row.querySelector('.nest-mode-hint');
            if (len >= 80) {
              if (!hint) {
                hint = document.createElement('span');
                hint.className = 'nest-mode-hint';
                hint.textContent = '↳ Enter to create as card';
                row.appendChild(hint);
              }
            } else if (hint) {
              hint.remove();
            }
            // @delegate char counter + warning
            let charCounter = row.querySelector('.delegate-char-counter');
            let charWarn = row.querySelector('.delegate-char-warn');
            if (hasDelegate) {
              if (!charCounter) {
                charCounter = document.createElement('span');
                charCounter.className = 'delegate-char-counter';
                row.appendChild(charCounter);
              }
              charCounter.textContent = len + '/80';
              charCounter.classList.toggle('over-limit', len >= 80);
              if (len >= 70) {
                if (!charWarn) {
                  charWarn = document.createElement('span');
                  charWarn.className = 'delegate-char-warn';
                  row.appendChild(charWarn);
                }
                charWarn.textContent = len >= 80
                  ? `\u26a0 Long task \u2014 ${AI_NAME} will create this as a card-level delegate`
                  : `Long task \u2014 ${AI_NAME} will treat this as a card-level delegate`;
                charWarn.classList.toggle('delegate-char-warn-hard', len >= 80);
              } else if (charWarn) {
                charWarn.remove();
              }
            } else {
              if (charCounter) charCounter.remove();
              if (charWarn) charWarn.remove();
            }
          });

          // ── @tag autocomplete dropdown ──
          const ctxDropdown = document.createElement('div');
          ctxDropdown.className = 'ctx-autocomplete hidden';
          addInput.parentElement.appendChild(ctxDropdown);
          addInput.addEventListener('input', () => {
            const val = addInput.value;
            const atIdx = val.lastIndexOf('@');
            if (atIdx < 0) { ctxDropdown.classList.add('hidden'); return; }
            const partial = val.slice(atIdx + 1).toLowerCase();
            const allTags = collectBoardTags(boardData);
            const matches = allTags.filter(t => t.startsWith(partial) && t !== partial);
            // Put @delegate first with purple styling
            matches.sort((a, b) => a === 'delegate' ? -1 : b === 'delegate' ? 1 : 0);
            if (!matches.length) { ctxDropdown.classList.add('hidden'); return; }
            ctxDropdown.innerHTML = matches.map(t => `<button class="ctx-ac-item${t === 'delegate' ? ' ctx-ac-delegate' : ''}" data-tag="${t}" tabindex="-1">@${t}</button>`).join('');
            ctxDropdown.classList.remove('hidden');
          });
          ctxDropdown.addEventListener('mousedown', (e) => {
            const item = e.target.closest('.ctx-ac-item');
            if (!item) return;
            e.preventDefault();
            const val = addInput.value;
            const atIdx = val.lastIndexOf('@');
            addInput.value = val.slice(0, atIdx) + '@' + item.dataset.tag;
            ctxDropdown.classList.add('hidden');
            addInput.focus();
          });
          addInput.addEventListener('blur', () => setTimeout(() => ctxDropdown.classList.add('hidden'), 150));

          card.querySelectorAll('.task-delete-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              const taskId = btn.dataset.taskId; const ov2 = loadOverrides();
              if (!ov2.deletedTaskIds) ov2.deletedTaskIds = [];
              if (!ov2.deletedTaskIds.includes(taskId)) ov2.deletedTaskIds.push(taskId);
              if (ov2.addedTasks && ov2.addedTasks[proj.id]) ov2.addedTasks[proj.id] = ov2.addedTasks[proj.id].filter(t => t.id !== taskId);
              saveOverrides(ov2); renderFromState();
            });
          });

          // @delegate artifact action buttons
          function performApprove(btn, taskId, isCardLevel, delegateId) {
            // BUG-13: Record delegate history
            const _artOv = loadOverrides();
            const _art = _artOv.delegateArtifacts?.[taskId];
            if (_art) {
              const _cardId = isCardLevel ? taskId.slice(5) : proj.id;
              writeDelegateHistory(taskId, _cardId, _art.type || 'result', 'approved');
            }
            if (delegateId) {
              const hdrs = getAuthHeader();
              hdrs['Content-Type'] = 'application/json';
              fetch(`/api/v1/delegate/approve/${delegateId}`, {
                method: 'POST',
                headers: hdrs,
                body: JSON.stringify({}),
              }).then(r => r.json()).then(res => {
                if (res.status === 'approved_pending_send') {
                  const ov2 = loadOverrides();
                  if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
                  saveOverrides(ov2);
                  renderFromState();
                  const banner = btn.closest('.delegate-artifact') || btn.closest('.card-artifact-banner');
                  if (banner) { banner.innerHTML = '<span style="color:#bb9af7;font-size:0.82rem">\u{1f4e7} Email queued for sending\u2026</span>'; setTimeout(() => { renderFromState(); }, 2500); }
                } else {
                  const ov2 = loadOverrides();
                  if (isCardLevel) {
                    const cardId = taskId.slice(5);
                    if (!ov2.cardStatus) ov2.cardStatus = {};
                    ov2.cardStatus[cardId] = "Done";
                  } else {
                    if (!ov2.taskStates) ov2.taskStates = {};
                    ov2.taskStates[taskId] = true;
                    ov2.taskCompletedAt = ov2.taskCompletedAt || {};
                    ov2.taskCompletedAt[taskId] = new Date().toISOString();
                    if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
                    ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
                  }
                  if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
                  saveOverrides(ov2);
                  renderWins && renderWins();
                  renderFromState();
                }
              }).catch(e => console.warn('[delegate] approve failed:', e.message));
            } else {
              const ov2 = loadOverrides();
              if (isCardLevel) {
                const cardId = taskId.slice(5);
                if (!ov2.cardStatus) ov2.cardStatus = {};
                ov2.cardStatus[cardId] = "Done";
              } else {
                if (!ov2.taskStates) ov2.taskStates = {};
                ov2.taskStates[taskId] = true;
                ov2.taskCompletedAt = ov2.taskCompletedAt || {};
                ov2.taskCompletedAt[taskId] = new Date().toISOString();
                if (!ov2.taskDoneOverrides) ov2.taskDoneOverrides = {};
                ov2.taskDoneOverrides[taskId] = { done: true, doneAt: new Date().toISOString() };
              }
              if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
              saveOverrides(ov2);
              renderWins && renderWins();
              renderFromState();
            }
          }

          card.querySelectorAll('.delegate-btn-approve').forEach(btn => {
            btn.addEventListener('click', async (e) => {
              e.stopPropagation();
              const taskId = btn.dataset.artifactTaskId;
              const isCardLevel = taskId.startsWith('card:');
              const ov = loadOverrides();
              const artifact = ov.delegateArtifacts?.[taskId];
              const delegateId = artifact?.delegateId || btn.dataset.delegateId;
              const artifactType = btn.dataset.artifactType;

              // Intercept email drafts with confirmation dialog
              if (artifactType === 'email_draft' && artifact) {
                showSendConfirmModal(artifact, () => performApprove(btn, taskId, isCardLevel, delegateId));
                return;
              }

              performApprove(btn, taskId, isCardLevel, delegateId);
            });
          });

          // "Save only" button for email_draft — approve without triggering send gate
          card.querySelectorAll('.delegate-btn-save-only').forEach(btn => {
            btn.addEventListener('click', async (e) => {
              e.stopPropagation();
              const taskId = btn.dataset.artifactTaskId;
              const isCardLevel = taskId.startsWith('card:');
              const ov = loadOverrides();
              const artifact = ov.delegateArtifacts?.[taskId];
              const delegateId = artifact?.delegateId;

              // Notify server with saveOnly flag — sets approved_pending_send without send
              if (delegateId) {
                const hdrs = getAuthHeader();
                hdrs['Content-Type'] = 'application/json';
                fetch(`/api/v1/delegate/approve/${delegateId}`, {
                  method: 'POST',
                  headers: hdrs,
                  body: JSON.stringify({ saveOnly: true }),
                }).then(r => r.json()).then(res => {
                  // Save only — remove artifact banner, do NOT mark task done
                  const ov2 = loadOverrides();
                  if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
                  saveOverrides(ov2);
                  renderFromState();
                }).catch(e => console.warn('[delegate] save-only failed:', e.message));
              } else {
                const ov2 = loadOverrides();
                if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
                saveOverrides(ov2);
                renderFromState();
              }
            });
          });

          card.querySelectorAll('.delegate-btn-discard').forEach(btn => {
            btn.addEventListener('click', async (e) => {
              e.stopPropagation();
              const taskId = btn.dataset.artifactTaskId;
              const ov = loadOverrides();
              const artifact = ov.delegateArtifacts?.[taskId];
              const delegateId = artifact?.delegateId || btn.dataset.delegateId;
              const artifactSnapshot = artifact ? JSON.parse(JSON.stringify(artifact)) : null;

              // Remove artifact from overrides (optimistic UI)
              const ov2 = loadOverrides();
              if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
              saveOverrides(ov2); renderFromState();

              // Show undo toast — API call deferred 3 seconds
              showDiscardUndoToast(taskId, delegateId, artifactSnapshot);
            });
          });
          // Cap history toggle
          const capHistToggle = card.querySelector('.cap-history-toggle');
          if (capHistToggle) {
            capHistToggle.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const list = card.querySelector('.cap-history-list');
              if (list) list.classList.toggle('hidden');
            });
          }

          // Snooze button handler
          card.querySelectorAll('.delegate-btn-snooze').forEach(btn => {
            btn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const taskId = btn.dataset.artifactTaskId;
              // Show inline snooze options
              let menu = btn.parentElement.querySelector('.snooze-menu');
              if (menu) { menu.remove(); return; }
              menu = document.createElement('div');
              menu.className = 'snooze-menu';
              menu.innerHTML = `<button data-snooze="2h">2 hours</button><button data-snooze="tonight">Tonight (9pm)</button><button data-snooze="tomorrow">Tomorrow (8am)</button>`;
              btn.parentElement.appendChild(menu);
              menu.querySelectorAll('button').forEach(opt => {
                opt.addEventListener('click', (ev2) => {
                  ev2.stopPropagation();
                  snoozeArtifact(taskId, opt.dataset.snooze);
                });
              });
              // Close on outside click
              const closeMenu = (ev2) => { if (!menu.contains(ev2.target) && ev2.target !== btn) { menu.remove(); document.removeEventListener('click', closeMenu, true); } };
              setTimeout(() => document.addEventListener('click', closeMenu, true), 0);
            });
          });

          // "Try again" button for empty-result artifacts — re-queue @delegate
          card.querySelectorAll('.delegate-btn-retry').forEach(btn => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              const taskId = btn.dataset.taskId;
              const cardId = btn.dataset.cardId;
              const cardName = btn.dataset.cardName;
              const taskText = btn.dataset.taskText;
              // Remove the empty artifact
              const ov2 = loadOverrides();
              if (ov2.delegateArtifacts) delete ov2.delegateArtifacts[taskId];
              saveOverrides(ov2);
              // Re-enqueue to delegate API
              fetch('/api/v1/delegate', {
                method: 'POST',
                headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ cardId, cardName, taskId, taskText, cardScope: proj.outcome || proj.scope || '', cardNotes: '' }),
              }).catch(err => console.warn('[delegate] retry enqueue failed:', err.message));
              renderFromState();
            });
          });

          // View Full → open workspace focused on this artifact
          card.querySelectorAll('.delegate-btn-view').forEach(btn => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              const taskId = btn.dataset.artifactTaskId;
              openWorkspace(proj.id, { taskId });
            });
          });

          // Edit → open workspace (edit happens in workspace right panel)
          card.querySelectorAll('.delegate-btn-edit').forEach(btn => {
            btn.addEventListener('click', (ev) => {
              ev.stopPropagation();
              const taskId = btn.dataset.artifactTaskId;
              openWorkspace(proj.id, { taskId, mode: 'edit' });
            });
          });

          // LEGACY: keep approve handler working from card face (non-email approves don't need workspace)
          // Already wired above via .delegate-btn-approve handlers

          /* Removed: old inline edit flow — replaced by workspace mode */

          card.querySelectorAll('.task-set-date, .task-due-pill').forEach(el => {
            el.addEventListener('click', (e) => {
              e.stopPropagation();
              const taskId = el.dataset.taskId || el.closest('.task-item')?.dataset.taskId;
              if (!taskId) return;
              const ov2 = loadOverrides();
              const existing = ov2.taskDueDates && ov2.taskDueDates[taskId] ? ov2.taskDueDates[taskId] : '';
              const input = document.createElement('input');
              input.type='text'; input.className='task-date-input'; input.placeholder='Apr 30 2026';
              input.addEventListener('click', ce => ce.stopPropagation());
              if (existing) { const d=new Date(existing+'T00:00:00'); const M=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; input.value=`${M[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`; }
              el.replaceWith(input); input.focus(); input.select();
              function parseDate(str) {
                if (!str.trim()) return null;
                const M={jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
                const now=new Date(); const cy=now.getFullYear();
                const mdy=str.match(/^([a-zA-Z]{3,})\s+(\d{1,2})(?:\s+(\d{4}))?$/);
                if (mdy) { const mo=M[mdy[1].toLowerCase().slice(0,3)]; if(!mo) return null; const day=parseInt(mdy[2]); let y=mdy[3]?parseInt(mdy[3]):cy; if(!mdy[3]){const c=new Date(cy,mo-1,day);if(c<now)y=cy+1;} return `${y}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`; }
                const sl=str.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
                if (sl) { const mo=parseInt(sl[1]); const day=parseInt(sl[2]); let y=sl[3]?(sl[3].length===2?2000+parseInt(sl[3]):parseInt(sl[3])):cy; if(!sl[3]){const c=new Date(cy,mo-1,day);if(c<now)y=cy+1;} return `${y}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`; }
                return null;
              }
              function saveDate() {
                const val=parseDate(input.value); const ov3=loadOverrides(); if(!ov3.taskDueDates) ov3.taskDueDates={};
                if(val){ov3.taskDueDates[taskId]=val;saveOverrides(ov3);renderFromState();}
                else if(!input.value.trim()){delete ov3.taskDueDates[taskId];saveOverrides(ov3);renderFromState();}
                else input.style.borderColor='var(--danger)';
              }
              input.addEventListener('keydown',(ke)=>{ke.stopPropagation();if(ke.key==='Enter'){ke.preventDefault();saveDate();}if(ke.key==='Escape')renderFromState();});
              input.addEventListener('blur',saveDate);
            });
          });

          card.querySelectorAll('.task-text').forEach(textSpan => {
            textSpan.addEventListener('click', (e) => {
              e.stopPropagation();
              if (textSpan.closest('.task-item').classList.contains('done')) return;
              const taskId=textSpan.dataset.taskId; const currentText=textSpan.textContent;
              const input=document.createElement('input'); input.type='text'; input.className='task-edit-input'; input.value=currentText;
              textSpan.replaceWith(input); input.focus(); input.select();
              function saveEdit() {
                const newText=input.value.trim();
                if(newText&&newText!==currentText){const ov2=loadOverrides();if(!ov2.taskTexts)ov2.taskTexts={};ov2.taskTexts[taskId]=newText;saveOverrides(ov2);renderFromState();}
                else input.replaceWith(textSpan);
              }
              input.addEventListener('blur',saveEdit);
              input.addEventListener('keydown',(ke)=>{ke.stopPropagation();if(ke.key==='Enter'){ke.preventDefault();saveEdit();}if(ke.key==='Escape'){input.removeEventListener('blur',saveEdit);input.replaceWith(textSpan);}});
              input.addEventListener('click',(ce)=>ce.stopPropagation());
            });
          });

          // Archive handled by card-archive-icon-btn on card face

                    // ── Day 6: Task owner pill click → inline edit ──
          card.querySelectorAll('.task-owner-pill').forEach(pill => {
            pill.addEventListener('click', (e) => {
              e.stopPropagation();
              const taskId = pill.dataset.taskId;
              const input = document.createElement('input');
              input.type = 'text'; input.className = 'task-owner-input';
              input.placeholder = 'Owner name'; input.value = pill.textContent.replace(/^@/, '');
              pill.replaceWith(input); input.focus(); input.select();
              function saveOwner() {
                const val = input.value.trim();
                const ov2 = loadOverrides();
                if (!ov2.taskOwners) ov2.taskOwners = {};
                if (val) ov2.taskOwners[taskId] = val;
                else delete ov2.taskOwners[taskId];
                saveOverrides(ov2); renderFromState();
              }
              input.addEventListener('blur', saveOwner);
              input.addEventListener('keydown', (ke) => {
                ke.stopPropagation();
                if (ke.key === 'Enter') { ke.preventDefault(); saveOwner(); }
                if (ke.key === 'Escape') renderFromState();
              });
              input.addEventListener('click', ce => ce.stopPropagation());
            });
          });

          // ── Day 7: Outcome field save ──
          const outcomeField = card.querySelector('.outcome-field');
          if (outcomeField) {
            outcomeField.addEventListener('input', () => {
              const ov2 = loadOverrides();
              if (!ov2.cardOutcomes) ov2.cardOutcomes = {};
              ov2.cardOutcomes[proj.id] = outcomeField.value;
              saveOverrides(ov2); // debounced cloud save
            });
            outcomeField.addEventListener('click', e => e.stopPropagation());
            outcomeField.addEventListener('keydown', e => e.stopPropagation());
            // ── @delegate detection in scope field ──
            if (!isParent) {
              outcomeField.addEventListener('blur', () => {
                const val = outcomeField.value;
                const match = val.match(/^@delegate\s+(.+)/is);
                if (!match) return;
                const instruction = match[1].trim();
                if (!instruction) return;
                // Clear @delegate text from field, preserve nothing (instruction consumed the rest)
                outcomeField.value = '';
                const ov2 = loadOverrides();
                if (!ov2.cardOutcomes) ov2.cardOutcomes = {};
                ov2.cardOutcomes[proj.id] = '';
                saveOverrides(ov2);
                enqueueCardDelegate(proj, instruction);
                showDelegateToast(`Delegated to ${AI_NAME} \u2197`);
              });
            }
          }

          // ── Day 8: Waiting For field save ──
          const waitingForField = card.querySelector('.waiting-for-field');
          if (waitingForField) {
            waitingForField.addEventListener('input', () => {
              const ov2 = loadOverrides();
              if (!ov2.cardWaitingFor) ov2.cardWaitingFor = {};
              ov2.cardWaitingFor[proj.id] = waitingForField.value;
              saveOverrides(ov2);
            });
            waitingForField.addEventListener('click', e => e.stopPropagation());
            waitingForField.addEventListener('keydown', e => e.stopPropagation());
          }

          // ── Section pill tabs (radio-style, remembers last open per card) ──
          card.querySelectorAll('.sec-pill').forEach(btn => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              const sec = btn.dataset.sec;
              const ov2 = loadOverrides();
              if (!ov2.cardActiveSection) ov2.cardActiveSection = {};
              // Toggle: clicking active pill closes it
              ov2.cardActiveSection[proj.id] = (openSec === sec) ? null : sec;
              saveOverrides(ov2);
              // Lazy-load comms when tab is opened and comms are externalised
              if (sec === 'comms' && boardData?._commsExternalised && !_commsCache[proj.id]) {
                fetchComms(proj.id).then(() => renderFromState());
                return;
              }
              renderFromState();
            });
          });

          // ── Day 8: Show all sections toggle ──
          const showAllToggle = card.querySelector('.card-show-all-toggle');
          if (showAllToggle) {
            showAllToggle.addEventListener('click', (e) => {
              e.stopPropagation();
              const ov2 = loadOverrides();
              if (!ov2.cardShowAllSections) ov2.cardShowAllSections = {};
              ov2.cardShowAllSections[proj.id] = !showAll;
              saveOverrides(ov2);
              renderFromState();
            });
          }

          // ── Day 11: Progress Notes ──
          const pnInput = card.querySelector('.progress-note-input');
          const pnAddBtn = card.querySelector('.progress-note-add-btn');
          if (pnInput && pnAddBtn) {
            function addProgressNote() {
              const text = pnInput.value.trim();
              if (!text) return;
              const ov2 = loadOverrides();
              if (!ov2.cardProgressNotes) ov2.cardProgressNotes = {};
              if (!ov2.cardProgressNotes[proj.id]) ov2.cardProgressNotes[proj.id] = [];
              ov2.cardProgressNotes[proj.id].push({ text, timestamp: new Date().toISOString() });
              if (ov2.cardProgressNotes[proj.id].length > 30) ov2.cardProgressNotes[proj.id] = ov2.cardProgressNotes[proj.id].slice(-30);
              saveOverrides(ov2);
              pnInput.value = '';
              setPendingTaskFocusId(null);
              renderFromState();
            }
            pnAddBtn.addEventListener('click', (e) => { e.stopPropagation(); addProgressNote(); });
            pnInput.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); addProgressNote(); } });
            pnInput.addEventListener('click', (e) => e.stopPropagation());
          }

          if (isParent) {
            card.querySelector('.card-nested-indicator').addEventListener('click', (e) => {
              e.stopPropagation();
              const isCurrentTarget = navStack.length > 0 && navStack[navStack.length - 1].id === proj.id;
              if (isCurrentTarget) { navStack.pop(); renderFromState(); }
              else { drillInto(proj.id, proj.name); }
            });
          } else {
            card.querySelector('.card-nest-btn').addEventListener('click', (e) => {
              e.stopPropagation();
              const ov2 = loadOverrides();
              if (!ov2.convertedToParent) ov2.convertedToParent = {};
              ov2.convertedToParent[proj.id] = { subBoardColumns: JSON.parse(JSON.stringify(DEFAULT_SUB_COLUMNS)) };
              const children = (proj.tasks || []).map((t, i) => ({
                id: proj.id + '-child-' + Date.now() + '-' + i,
                name: t.text, color: PRESET_COLORS[i % PRESET_COLORS.length],
                createdAt: new Date().toISOString().split('T')[0],
                columnId: t.done ? 'done' : 'todo',
                tasks: [], status: t.done ? 'Done' : '', contacts: [], comms: []
              }));
              if (!ov2.addedChildren) ov2.addedChildren = {};
              if (!ov2.addedChildren[proj.id]) ov2.addedChildren[proj.id] = [];
              ov2.addedChildren[proj.id].push(...children);
              saveOverrides(ov2);
              drillInto(proj.id, proj.name);
            });
          }

                // ── Cap badge tooltip (common) ──
        const capBadge = card.querySelector('.cap-badge');
        if (capBadge) {
          capBadge.addEventListener('click', (e) => {
            e.stopPropagation();
            let tooltip = capBadge.querySelector('.cap-tooltip');
            if (tooltip) {
              tooltip.remove();
            } else {
              tooltip = document.createElement('div');
              tooltip.className = 'cap-tooltip';
              tooltip.textContent = `Updated by ${AI_NAME} \u2014 ${formatCapDate(capBadge.dataset.capDate)}`;
              capBadge.appendChild(tooltip);
              setTimeout(() => tooltip.remove(), 3000);
            }
          });
        }

        // Archive and Delete now live in expanded section / archived card render only

        cardsContainer.appendChild(card);
      }

      // ── Empty state hint ──
      if (!isDrilled && cardsContainer.querySelectorAll('.card:not(.card-archived)').length === 0) {
        const emptyHint = document.createElement('button');
        emptyHint.className = 'column-empty-hint';
        emptyHint.textContent = 'No projects here yet — press N to add one';
        emptyHint.addEventListener('click', () => { openCapture(); setTimeout(() => { const colSel = document.getElementById('capture-column'); if (colSel) colSel.value = col.id; }, 50); });
        cardsContainer.appendChild(emptyHint);
      }

      if (!isDrilled) {
        // ── Add card button (main board only) ──
        const addCardBtn = document.createElement('button');
        addCardBtn.className = 'add-card-btn';
        addCardBtn.textContent = '+ Add Card';

        // Instant-create: add empty card and immediately trigger inline rename
        const colId = col.id;
        addCardBtn.addEventListener('click', () => {
          const newId = 'custom-' + Date.now();
          addNewProjectWithId(colId, newId, 'New Card', PRESET_COLORS[0], false);
          setPendingCardRenameId(newId);
          renderFromState();
          saveCardOrder();
        });

        colEl.querySelector('.column-footer').appendChild(addCardBtn);
        board.appendChild(colEl);
      }

      createCardSortable(cardsContainer);
    }

    // ── Column drag-to-reorder ──
    if (!isDrilled) {
      createColumnSortable(board);
    }

    // ── + Add Column (inline input, no prompt()) ──
    if (!isDrilled) {
      const addColWrapper = document.createElement('div');
      addColWrapper.className = 'add-column-wrapper';

      const addColBtn = document.createElement('button');
      addColBtn.className = 'add-column-btn';
      addColBtn.textContent = '+ Area';

      const addColForm = document.createElement('div');
      addColForm.className = 'add-column-form hidden';
      addColForm.innerHTML = `
        <input type="text" class="add-column-input" placeholder="Area name…" maxlength="40">
        <div class="add-column-actions">
          <button class="add-column-submit">Add</button>
          <button class="add-column-cancel">Cancel</button>
        </div>
      `;

      addColBtn.addEventListener('click', () => {
        addColBtn.classList.add('hidden');
        addColForm.classList.remove('hidden');
        addColForm.querySelector('.add-column-input').focus();
      });

      const submitCol = () => {
        const name = addColForm.querySelector('.add-column-input').value.trim();
        if (name) addColumn(name);
        else renderFromState();
      };

      addColForm.querySelector('.add-column-submit').addEventListener('click', submitCol);
      addColForm.querySelector('.add-column-cancel').addEventListener('click', () => renderFromState());
      addColForm.querySelector('.add-column-input').addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') { e.preventDefault(); submitCol(); }
        if (e.key === 'Escape') renderFromState();
      });
      addColForm.querySelector('.add-column-input').addEventListener('click', e => e.stopPropagation());

      addColWrapper.appendChild(addColBtn);
      addColWrapper.appendChild(addColForm);
      board.appendChild(addColWrapper);
    }
  }

  function updateContextTagSuggestions() {
    const merged = mergeOverrides(boardData);
    const tags = collectAllContextTags(merged);
    const defaults = ['call', 'desk', 'site', 'zoom', 'remote'];
    const all = [...new Set([...defaults, ...tags])].sort();
    let dl = document.getElementById('ctx-tag-suggestions');
    if (!dl) { dl = document.createElement('datalist'); dl.id = 'ctx-tag-suggestions'; document.body.appendChild(dl); }
    dl.innerHTML = all.map(t => `<option value="@${t} ">`).join('');
  }

  function renderFromState() {
    if (!boardData) return;
    // Focus Flow manages its own DOM — skip board rebuild
    if (isFocusFlowActive()) return;
    // Save column scroll positions before DOM rebuild
    const _scrollPositions = {};
    document.querySelectorAll('.column-cards[data-column-id]').forEach(el => {
      if (el.scrollTop) _scrollPositions[el.dataset.columnId] = el.scrollTop;
    });
    const merged = mergeOverrides(boardData);
    render(merged);
    // Restore column scroll positions after rebuild
    if (Object.keys(_scrollPositions).length) {
      document.querySelectorAll('.column-cards[data-column-id]').forEach(el => {
        if (_scrollPositions[el.dataset.columnId]) el.scrollTop = _scrollPositions[el.dataset.columnId];
      });
    }
    if (isIntentionsActive()) {
      setTimeout(applyIntentionSpotlight, 30);
    }
    updateContextTagSuggestions();
    if (_pendingTaskFocusId) {
      const focusId = _pendingTaskFocusId;
      setPendingTaskFocusId(null);
      const input = document.querySelector(`.add-task-input[data-project-id="${focusId}"]`);
      if (input) {
        const card = input.closest('.card');
        if (card && !card.classList.contains('expanded')) {
          card.classList.add('expanded');
          expandedCards.add(focusId);
          initTaskSortable(card.querySelector('.card-tasks-list'));
        }
        // Focus first, then restore scroll — browser auto-scrolls on focus and
        // would override our saved position if we restored it before calling focus().
        input.focus({ preventScroll: true });
        // Re-apply scroll positions after focus to prevent the column jumping to top
        if (Object.keys(_scrollPositions).length) {
          document.querySelectorAll('.column-cards[data-column-id]').forEach(el => {
            if (_scrollPositions[el.dataset.columnId]) el.scrollTop = _scrollPositions[el.dataset.columnId];
          });
        }
      }
    }
    // Inline rename for newly created cards (Add Card instant-create flow)
    if (_pendingCardRenameId) {
      const renameId = _pendingCardRenameId;
      setPendingCardRenameId(null);
      const cardEl = document.querySelector(`.card[data-project-id="${renameId}"]`);
      if (cardEl) {
        const nameEl = cardEl.querySelector('.card-name');
        if (nameEl) {
          // Simulate a dblclick to trigger the existing inline rename flow
          const dblclickEvt = new MouseEvent('dblclick', { bubbles: true, cancelable: true });
          nameEl.dispatchEvent(dblclickEvt);
          // Scroll new card into view so user can see the rename input
          cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
    // Workspace survival: if workspace is open, re-hide the card and refresh workspace content
    const activeWs = getActiveWorkspace();
    if (activeWs && document.getElementById('card-workspace')) {
      const wsCardEl = document.querySelector(`.card[data-project-id="${activeWs.cardId}"]`);
      if (wsCardEl) {
        wsCardEl.style.visibility = 'hidden';
        activeWs.cardEl = wsCardEl;
      }
      refreshWorkspace();
    }
    // Update dynamic AI_NAME in Cap Activity panel header
    const capTitleEl = document.getElementById('cap-activity-title');
    if (capTitleEl) capTitleEl.textContent = AI_NAME;
    // Update cap-activity-btn tooltip
    const capBtn = document.getElementById('cap-activity-btn');
    if (capBtn) capBtn.title = `${AI_NAME} activity`;
    // ── Initialize extracted modules ──
    initAuth({ boardDataRef: _boardDataRef });
    initStorage({
      getAuthHeader,
      getVersion: () => _localOverridesVersion,
      setVersion: (v) => { setLocalOverridesVersion(v); },
      getUserKey: () => localStorage.getItem(SESSION_USER_KEY) || 'default',
      softLogout,
      flashSaved,
      flashToast,
      renderFromState,
    });
    initCapture({
      getAuthHeader,
      getBoardData: () => boardData,
      getOverrides: loadOverrides,
      saveOverrides,
      renderFromState,
      mergeOverrides,
      loadOverrides,
      trapFocus,
      activeFocusTrapRef: _activeFocusTrapRef,
      focusTrapTriggerRef: _focusTrapTriggerRef,
      getAiName: () => AI_NAME,
    });

  }

export { render, renderBreadcrumb, drillInto, renderFromState, updateContextTagSuggestions };
